// Funciones core de cálculos para módulo de calidad
// Centraliza lógica usada en listado, formularios y visualización

const toNum = (v) => {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
};

const round2 = (n) => Number(n.toFixed(2));

// Análisis físico
export const calcRendimientoBruto = (pesoMuestraPergamino, pesoOroPergamino) => {
  const pm = toNum(pesoMuestraPergamino);
  const po = toNum(pesoOroPergamino);
  if (pm > 0 && po >= 0) return round2((po / pm) * 100);
  return '';
};

export const calcManchaPorcentaje = (pesoMuestraPergamino, pesoManchaGr) => {
  const pm = toNum(pesoMuestraPergamino);
  const df = toNum(pesoManchaGr);
  if (pm > 0 && df >= 0) return round2((df / pm) * 100);
  return '';
};

export const calcPesoCafeLimpio = (...args) => {
  // Firma por zarandas
  if (Array.isArray(args?.[0])) {
    const zarandasIds = Array.isArray(args[0]) ? args[0].map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
    const entries = Array.isArray(args[1]) ? args[1] : [];
    const totalDefectosGr = toNum(args[2]);
    if (!zarandasIds.length || !entries.length) return '';
    const sumaZarandas = entries
      .filter((z) => zarandasIds.includes(Number(z?.id_zaranda)))
      .reduce((acc, z) => acc + toNum(z?.valor), 0);
    return round2(Math.max(sumaZarandas - totalDefectosGr, 0));
  }

  // Firma legacy (por físico: oro - mancha)
  const pesoOroPergamino = args[0];
  const pesoManchaGr = args[1];
  const po = toNum(pesoOroPergamino);
  const df = toNum(pesoManchaGr);
  if (po >= 0 && df >= 0) return round2(Math.max(po - df, 0));
  return '';
};

export const calcRendimientoExportable = (pesoMuestraPergamino, pesoOroPergamino, pesoManchaGr) => {
  const pm = toNum(pesoMuestraPergamino);
  const limpio = calcPesoCafeLimpio(pesoOroPergamino, pesoManchaGr);
  if (pm > 0 && typeof limpio === 'number') return round2((limpio / pm) * 100);
  return '';
};

// Cálculo por zarandas: suma de valores (excluyendo IDs) menos defectos (gr)
export const calcPesoCafeLimpioPorZarandas = (entries = [], totalDefectosGr, excludeIds = []) => {
  const idsToExclude = Array.isArray(excludeIds) ? excludeIds.map((id) => Number(id)).filter((id) => !isNaN(id)) : [];
  const validEntries = Array.isArray(entries) ? entries.filter((z) => {
    const id = Number(z?.id_zaranda);
    if (isNaN(id)) return false;
    return !idsToExclude.includes(id);
  }) : [];
  const hasValues = validEntries.some((z) => toNum(z?.valor) > 0);
  if (!hasValues) return '';
  const sumaZarandas = validEntries.reduce((acc, z) => acc + toNum(z?.valor), 0);
  const df = toNum(totalDefectosGr);
  return round2(Math.max(sumaZarandas - df, 0));
};

// Rendimiento exportable a partir de peso limpio directamente
export const calcRendimientoExportableDesdeLimpio = (pesoMuestraPergamino, pesoCafeLimpio) => {
  const pm = toNum(pesoMuestraPergamino);
  const limpio = toNum(pesoCafeLimpio);
  if (pm > 0 && limpio >= 0) return round2((limpio / pm) * 100);
  return '';
};

// Análisis oro
export const calcDefectosManchaPor = (pesoOroMuestra2, totalDefectosGr) => {
  const po2 = toNum(pesoOroMuestra2);
  const df = toNum(totalDefectosGr);
  if (po2 > 0 && df >= 0) return round2((df / po2) * 100);
  return '';
};

export const calcTotalDefectosConteo = (defectosPrimarios = [], defectosSecundarios = []) => {
  const prim = Array.isArray(defectosPrimarios) ? defectosPrimarios.length : 0;
  const sec = Array.isArray(defectosSecundarios) ? defectosSecundarios.length : 0;
  return prim + sec;
};

export const calcTotalDefectosRedondeado = (defectosPrimarios = [], defectosSecundarios = []) => {
  const sumArray = arr => (Array.isArray(arr) ? arr.reduce((acc, d) => {
    let v = d?.defectos;
    if (typeof v === 'string') v = v.replace(',', '.');
    const n = Number(v);
    return acc + (isNaN(n) ? 0 : n);
  }, 0) : 0);
  const sum = sumArray(defectosPrimarios) + sumArray(defectosSecundarios);
  return Math.ceil(sum);
};

// Porcentaje por criba: valor / peso muestra oro * 100
export const calcPorcentajeZaranda = (valorCriba, pesoMuestraOro) => {
  const v = toNum(valorCriba);
  const p = toNum(pesoMuestraOro);
  if (p > 0 && v >= 0) return round2((v / p) * 100);
  return '';
};

export const collectDefectNames = (defectosPrimarios = [], defectosSecundarios = []) => {
  const primNames = Array.isArray(defectosPrimarios) ? defectosPrimarios.map((d) => d?.nombre_defecto ?? '').filter(Boolean) : [];
  const secNames = Array.isArray(defectosSecundarios) ? defectosSecundarios.map((d) => d?.nombre_defecto ?? '').filter(Boolean) : [];
  return [...primNames, ...secNames].join(', ');
};

export const calcRendimientoReal = (pesoMuestraPergamino, pesoOroPergamino, pesoManchaGr, perdidaProcesamientoPor) => {
  // Rendimiento real = rendimiento exportable - descuento por procesamiento
  const rexp = calcRendimientoExportable(pesoMuestraPergamino, pesoOroPergamino, pesoManchaGr);
  const descuento = toNum(perdidaProcesamientoPor);
  if (typeof rexp === 'number') return round2(rexp - descuento);
  return '';
};

// Análisis sensorial: cálculo de "Defectos" (número de tazas con defecto * intensidad)
export const calcDefectosScore = (defectosNoTazas, defectosIntensidad) => {
  const nt = toNum(defectosNoTazas);
  const it = toNum(defectosIntensidad);
  const score = nt * it;
  return score > 0 ? String("- " + score) : '';
};

// Análisis sensorial: cálculo del puntaje total en taza
// suma de atributos (excepto uniformidad, dulzor, taza limpia) + (uniformidad*2) + (dulzor*2) + (taza_limpia*2)
// menos (defectos_no_tazas * defectos_intensidad)
export const calcPuntajeSensorial = (sensorial = {}) => {
  const keys = [
    'fragancia_aroma', 'sabor', 'sabor_residual', 'acidez', 'cuerpo', 'balance', 'catador'
  ];
  const suma = keys.reduce((acc, k) => acc + toNum(sensorial?.[k]), 0);
  const uniformidad = toNum(sensorial?.uniformidad);
  const dulzor = toNum(sensorial?.dulzor);
  const tazaLimpia = toNum(sensorial?.taza_limpia);
  const defectosNoTazas = toNum(sensorial?.defectos_no_tazas);
  const defectosIntensidad = toNum(sensorial?.defectos_intensidad);
  const total = (suma + (uniformidad * 2) + (dulzor * 2) + (tazaLimpia * 2)) - (defectosNoTazas * defectosIntensidad);
  return round2(total);
};

// Utilidades de formateo para UI
export const withSuffix = (value, suffix) => {
  if (value === '' || value == null) return '-';
  return `${value} ${suffix || ''}`.trim();
};

export const numberOrEmpty = (value) => {
  const n = Number(value);
  return isNaN(n) ? '' : n;
};
